// Utilidades de fechas. Regla: los instantes se guardan en UTC (ISO); las fechas
// "de calendario" (cumpleaños, vencimientos) se manejan como texto YYYY-MM-DD
// para no sufrir corrimientos por zona horaria.

import type { RecurringFrequency, TaskRepeat } from './enums';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateOnly(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
  return d.getUTCFullYear() === +m[1]! && d.getUTCMonth() === +m[2]! - 1 && d.getUTCDate() === +m[3]!;
}

function parts(dateOnly: string): [number, number, number] {
  const m = DATE_RE.exec(dateOnly);
  if (!m || !isDateOnly(dateOnly)) throw new Error(`Fecha inválida: ${dateOnly}`);
  return [+m[1]!, +m[2]!, +m[3]!];
}

function fmt(y: number, mo: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysInMonth(y: number, mo: number): number {
  return new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

export function addDays(dateOnly: string, n: number): string {
  const [y, mo, d] = parts(dateOnly);
  const t = new Date(Date.UTC(y, mo - 1, d + n));
  return fmt(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** Suma meses respetando fin de mes: 2025-01-31 + 1 mes = 2025-02-28. */
export function addMonthsClamped(dateOnly: string, n: number): string {
  const [y, mo, d] = parts(dateOnly);
  const total = y * 12 + (mo - 1) + n;
  const ny = Math.floor(total / 12);
  const nmo = (total % 12) + 1;
  return fmt(ny, nmo, Math.min(d, daysInMonth(ny, nmo)));
}

export function nextTaskDate(dateOnly: string, repeat: TaskRepeat): string | null {
  switch (repeat) {
    case 'none': return null;
    case 'daily': return addDays(dateOnly, 1);
    case 'weekly': return addDays(dateOnly, 7);
    case 'monthly': return addMonthsClamped(dateOnly, 1);
    case 'yearly': return addMonthsClamped(dateOnly, 12);
  }
}

/**
 * Siguiente fecha de una donación recurrente. En las mensuales, `dayOfMonth` es el día original
 * (p. ej. 31): así enero 31 → febrero 28 → marzo 31, sin ir corriéndose al 28.
 */
export function nextRecurringDate(dateOnly: string, freq: RecurringFrequency, dayOfMonth?: number | null): string {
  switch (freq) {
    case 'weekly': return addDays(dateOnly, 7);
    case 'every_two_weeks': return addDays(dateOnly, 14);
    case 'monthly': {
      const next = addMonthsClamped(dateOnly, 1);
      if (!dayOfMonth) return next;
      const [y, m] = parts(next);
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return `${next.slice(0, 8)}${String(Math.min(dayOfMonth, last)).padStart(2, '0')}`;
    }
  }
}

/** Edad en meses cumplidos en una fecha dada. */
export function ageInMonths(birthdate: string, on: string): number {
  const [by, bm, bd] = parts(birthdate);
  const [y, m, d] = parts(on);
  let months = (y - by) * 12 + (m - bm);
  if (d < bd) months -= 1;
  return months;
}

export function ageInYears(birthdate: string, on: string): number {
  return Math.floor(ageInMonths(birthdate, on) / 12);
}

/** Edad legible: "8 meses", "1 año", "5 años" (meses hasta los 2 años). En inglés: "8 months", "5 years". */
export function ageLabel(birthdate: string, on: string, locale: 'es' | 'en' = 'es'): string {
  const m = Math.max(0, ageInMonths(birthdate, on));
  if (m < 24) {
    if (locale === 'en') return m === 1 ? '1 month' : `${m} months`;
    return m === 1 ? '1 mes' : `${m} meses`;
  }
  const y = Math.floor(m / 12);
  return locale === 'en' ? `${y} years` : `${y} años`;
}

/** Grado escolar: -1 = Pre-K, 0 = Kínder, 1..12. */
export function gradeLabel(grade: number | null | undefined, locale: 'es' | 'en' = 'es'): string {
  if (grade === null || grade === undefined) return '';
  if (grade === -1) return 'Pre-K';
  if (grade === 0) return locale === 'es' ? 'Kínder' : 'Kindergarten';
  return locale === 'es' ? `${grade}.º grado` : `Grade ${grade}`;
}

// ---- Zonas horarias ----

function offsetMinutes(instantMs: number, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p: Record<string, number> = {};
  for (const part of dtf.formatToParts(new Date(instantMs))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
  return Math.round((asUtc - instantMs) / 60000);
}

/**
 * Convierte una hora local de un campus a instante UTC (ISO).
 * zonedToUtc("2026-11-01", "09:00", "America/New_York") -> "2026-11-01T14:00:00.000Z"
 */
export function zonedToUtc(dateOnly: string, time: string, timeZone: string): string {
  const [y, mo, d] = parts(dateOnly);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!tm) throw new Error(`Hora inválida: ${time}`);
  const wall = Date.UTC(y, mo - 1, d, +tm[1]!, +tm[2]!);
  let guess = wall - offsetMinutes(wall, timeZone) * 60000;
  // Segunda pasada para los cambios de horario de verano.
  guess = wall - offsetMinutes(guess, timeZone) * 60000;
  return new Date(guess).toISOString();
}

/** Fecha de calendario (YYYY-MM-DD) de un instante en una zona horaria. */
export function localDate(iso: string | Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  return dtf.format(typeof iso === 'string' ? new Date(iso) : iso);
}

/** Hora local HH:MM de un instante en una zona horaria. */
export function localTime(iso: string | Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return dtf.format(typeof iso === 'string' ? new Date(iso) : iso);
}

/** Día de la semana (0=domingo) de una fecha de calendario. */
export function weekday(dateOnly: string): number {
  const [y, m, d] = parts(dateOnly);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
